import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { TripCard } from "@/features/trips/TripCard";
import { getTrip, tripsKeys } from "@/features/trips/trips.api";
import { Skeleton } from "@/components/feedback/Skeleton";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/trips/$id")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_planning") && !hasModule(context.session, "tms_planning_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  component: TripCardScreen,
});

function TripCardScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { session } = Route.useRouteContext();
  const canPlan = session.roles.includes("admin") || hasModule(session, "tms_planning_write");

  const query = useQuery({
    queryKey: tripsKeys.detail(id),
    queryFn: () => getTrip(id),
    retry: false,
    // A trip is planned by more than one person; what a colleague adds shows up.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  if (query.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office Trip" aria-busy="true">
        <Skeleton width="80px" height="var(--space-3)" />
        <div style={{ marginTop: "var(--space-2_5)" }}>
          <Skeleton width="240px" height="var(--space-7)" />
        </div>
        <div style={{ marginTop: "var(--space-5)" }}>
          <Skeleton height="260px" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Trip">
        <h1 className="t-h2">{t("trips.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("trips.notFoundDesc")}
        </p>
      </div>
    );
  }

  return <TripCard trip={query.data} canPlan={canPlan} />;
}
